/**
 * quotaError — Auswertung der 402-Quota-Payload (Story 6.1 AC3 / 6.3)
 *
 * Eigenes Modul (nicht in AddHotspotModal.tsx), damit die Komponentendatei
 * ausschliesslich Komponenten exportiert (react-refresh/only-export-components).
 */
import { apiErrorCode, apiErrorStatus } from '../../lib/errors/apiErrorMessage'

/**
 * Story 6.1 AC3 / 6.3 — strukturierte 402-Quota-Payload des Backends:
 *   { error, code: 'quota_exceeded', device_type, used, limit, upgrade_url }
 * (HotspotQuotaEnforcer.quota_payload). Statt eines generischen Fehlertexts
 * zeigt das Modal daraus ein Upsell mit Verbrauch/Limit und Upgrade-CTA.
 */
export type QuotaInfo = {
  deviceType?: 'mobile' | 'iot'
  used?: number
  limit?: number
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

function bodyOf(err: unknown): Record<string, unknown> {
  if (isRecord(err) && isRecord((err as { body?: unknown }).body)) {
    return (err as { body: Record<string, unknown> }).body
  }
  return isRecord(err) ? err : {}
}

function numberOrUndefined(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/**
 * Erkennt die Quota-Absage. Der `code` liegt bei dieser Payload TOP-LEVEL
 * (nicht im v2-Envelope `{error:{code}}`) — `apiErrorCode` deckt beide Formen ab.
 * Der Status-Check allein genuegt nicht, weil 402 auch von anderen
 * Premium-Gates kommen kann.
 */
export function quotaInfoFromError(err: unknown): QuotaInfo | null {
  if (apiErrorStatus(err) !== 402) return null
  if (apiErrorCode(err) !== 'quota_exceeded') return null

  const body = bodyOf(err)
  // `bucket` nennt das betroffene Kontingent ('mobile'/'iot'); `device_type`
  // traegt den rohen Geraetetyp ('cbrs', 'wifiOutdoor', 'iot', ...) und dient
  // nur als Fallback fuer aeltere Backend-Stufen ohne `bucket`.
  // Liefert das Backend `bucket` (auch als null bei unaufgeloestem Typ), ist das
  // die Wahrheit; sonst wird device_type klassifiziert.
  const raw = 'bucket' in body ? body.bucket : body.device_type
  const rawType = typeof raw === 'string' ? raw.toLowerCase() : undefined
  const MOBILE_TYPES = ['mobile', 'cbrs', 'wifioutdoor']
  const IOT_TYPES = ['iot', 'hotspot', 'wifiindoor', 'wifidatnly']

  return {
    deviceType: rawType
      ? MOBILE_TYPES.includes(rawType)
        ? 'mobile'
        : IOT_TYPES.includes(rawType)
          ? 'iot'
          : undefined
      : undefined,
    used: numberOrUndefined(body.used),
    limit: numberOrUndefined(body.limit),
  }
}
